import React, { useState, FocusEvent } from 'react';
import 'dayjs/locale/fr';
import 'dayjs/locale/es-us';
import { LocalizationProvider, MobileDatePicker as MUIDatePicker, PickersActionBarProps } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import formCSS from '../Form/FormWrapper/FormWrapper.module.scss';
import css from './DatePicker.module.scss';
import classNames from 'classnames';
import { Clear, Event as CalendarIcon } from '@mui/icons-material';
import { IconButton, InputProps } from '@mui/material';
import { useLocale, useLocalization } from '../../context/Localization/localization';
import dayjs from 'dayjs';
import { FieldHelperProps, FieldInputProps, FieldMetaProps } from 'formik';
import { GMFormWrapper } from '../Form';
import { Locales } from '../../context/Localization/localization.types';
import { GMButton } from '../Button';

export interface IBaseFieldProps<T = Element> {
    onFocus?: (e?: FocusEvent<T>) => void;
    onBlur?: (e: FocusEvent<T>) => void;
    onChange?: (e: any) => void;
}

export interface IBaseFormFieldProps<Val = any> extends IBaseFieldProps {
    name: string;
    label?: string;
    useFieldSettings?: [FieldInputProps<Val>, FieldMetaProps<Val>, FieldHelperProps<Val>];
    inputProps?: InputProps['inputProps'] & { type?: 'email' | 'tel' | 'number' | 'pin' | 'price' | 'password'; };
}

interface IProps extends Omit<IBaseFormFieldProps, 'onChange'> {
    value?: Date;
    labelClass?: string;
    onChange: (value?: Date) => void;
}

export const DatePicker = (props: IProps) => {
    const [open, setOpen] = useState<boolean>(false);
    const onClear = (event?: React.MouseEvent<HTMLButtonElement>) => {
        event?.preventDefault();
        event?.stopPropagation();

        props.onChange();
    };

    const locale = useLocale();
    const Localization = useLocalization();

    return (
        <GMFormWrapper fullWidth>
            <LocalizationProvider adapterLocale={locale === Locales.es ? 'es-us' : locale === Locales.fr ? 'fr' : undefined} dateAdapter={AdapterDayjs}>
                <MUIDatePicker
                    selectedSections={null}
                    label={props.label}
                    value={props.value ? dayjs(props.value) : null}
                    onAccept={(date) => props.onChange(date?.toDate())}
                    views={['year', 'day']}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    closeOnSelect
                    open={open}
                    slotProps={{
                        field: {
                            clearable: true,
                        },
                        textField: {
                            variant: 'standard',
                            InputLabelProps: {
                                classes: { root: classNames(formCSS.label, props.labelClass),
                                    focused: formCSS.focused,
                                    error: formCSS.error },
                                shrink: !!props.value,
                            },
                            InputProps: {
                                classes: { root: formCSS.input,
                                    error: formCSS.error,
                                    input: classNames(formCSS.baseInput, css.input) },
                                endAdornment: (
                                    <span className={formCSS.inputEndAdornmentWrapper}>
                                        {props.value && (
                                            <IconButton
                                                classes={{ root: formCSS.endAdornmentIconButton }}
                                                onClick={onClear}
                                                size={'large'}
                                            >
                                                <Clear fontSize={'small'} />
                                            </IconButton>
                                        )}
                                        <IconButton classes={{ root: formCSS.endAdornmentIconButton }} size={'large'}>
                                            <CalendarIcon />
                                        </IconButton>
                                    </span>
                                ),
                            },
                            FormHelperTextProps: { classes: { root: formCSS.errorMessage } },
                        },
                        calendarHeader: {
                            classes: {
                                labelContainer: css.headerLabel,
                            },
                        },
                        day: {
                            classes: {
                                root: css.day,
                                selected: css.selected,
                            },
                        },
                        yearButton: {
                            className: css.yearButton,
                        },
                    }}
                    slots={{
                        actionBar: (params: PickersActionBarProps) => (
                            <div className={params.className}>
                                <div className={css.actionBar}>
                                    <div className={css.actionBarButtons}>
                                        <GMButton
                                            id={Localization.Today}
                                            title={Localization.Today}
                                            ariaLabel={Localization.Today}
                                            onClick={params.onSetToday}
                                        >
                                            {Localization.Today}
                                        </GMButton>
                                        <GMButton
                                            id={Localization.Clear}
                                            title={Localization.Clear}
                                            ariaLabel={Localization.Clear}
                                            onClick={params.onClear}
                                        >
                                            {Localization.Clear}
                                        </GMButton>
                                    </div>
                                </div>
                            </div>
                        ),
                    }}
                    format={props.value ? (locale === Locales.fr ? 'YYYY/MM/DD' : 'MM/DD/YYYY') : ' '}
                    localeText={{
                        toolbarTitle: '',
                    }}
                    className={formCSS.textField}
                    maxDate={dayjs()}
                />
            </LocalizationProvider>
        </GMFormWrapper>
    );
};
